import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { getFirebaseConfig, useEmulators } from '../firebaseConfig';

// Studio-side Firebase init (full SDK - auth + firestore + storage). The game
// uses firebase/firestore/lite for a read-only one-shot; the studio needs
// writes + auth + uploads, so it pulls the full modular SDK. Lazily created on
// first use and cached.

export type StudioFirebase = {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  storage: FirebaseStorage;
};

let cached: StudioFirebase | null = null;

// True when the minimum config is present. main.tsx shows a setup notice
// instead of the portal when this is false.
export function isFirebaseConfigured(): boolean {
  return getFirebaseConfig() !== null;
}

export function getFirebase(): StudioFirebase {
  if (cached) return cached;
  const config = getFirebaseConfig();
  if (!config) throw new Error('Firebase is not configured');
  // Reuse an existing app (e.g. one created by loadAssetOverrides) - a second
  // initializeApp for the default app would throw.
  const app = getApps()[0] ?? initializeApp(config);
  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app);

  // Local development against the emulator suite (opt-in via env flag).
  if (useEmulators()) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
  }

  cached = { app, auth, db, storage };
  return cached;
}
